import { useState, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'wouter';
import { Layout } from '@/components/layout';
import { Button, Input, Select, Label, Drawer } from '@/components/ui-elements';
import { useToast } from '@/hooks/use-toast';
import { useAuthHeaders, useAuthStore } from '@/lib/auth-store';
import { fmtDateTime } from '@/lib/dateFormat';
import { Plus, Trash2, Zap, Store as StoreIcon, AlertCircle, CheckCircle2, ExternalLink } from 'lucide-react';
import { 
  useListStores, 
  useDeleteStore, 
  useTestStore,
  getListStoresQueryKey
} from '@workspace/api-client-react';

const BASE_URL = import.meta.env.BASE_URL.replace(/\/$/, "");

export function Stores() {
  const authOpts = useAuthHeaders();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [location, setLocation] = useLocation();

  const { data: stores, isLoading } = useListStores({ request: authOpts });
  const deleteMutation = useDeleteStore({ request: authOpts });
  const testMutation = useTestStore({ request: authOpts });

  // Handle OAuth callback query params
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("connected") === "1") {
      queryClient.invalidateQueries({ queryKey: getListStoresQueryKey() });
      toast({ title: "Store connected", description: "Your Shopify store has been successfully authorized." });
      window.history.replaceState({}, "", window.location.pathname);
    } else if (params.get("error")) {
      const errorMap: Record<string, string> = {
        invalid_state: "The OAuth session expired. Please try again.",
        shop_mismatch: "The store URL did not match. Please try again.",
        hmac_failed: "Could not verify the response from Shopify. Please try again.",
        token_exchange_failed: "Failed to get access token from Shopify. Check your Client ID and Secret.",
        save_failed: "Store was authorized but could not be saved. Please contact support.",
      };
      const msg = errorMap[params.get("error")!] || "An unexpected error occurred.";
      toast({ title: "OAuth Failed", description: msg, variant: "destructive" });
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  const handleDelete = async (id: string) => {
    if (!confirm("Are you sure you want to delete this store connection?")) return;
    try {
      await deleteMutation.mutateAsync({ id });
      queryClient.invalidateQueries({ queryKey: getListStoresQueryKey() });
      toast({ title: "Store deleted" });
    } catch (err: any) {
      toast({ title: "Error deleting store", description: err.message, variant: "destructive" });
    }
  };

  const handleTest = async (id: string) => {
    try {
      const res = await testMutation.mutateAsync({ id });
      if (res.success) {
        toast({ title: "Connection Successful", description: `Connected to ${res.shopName} (${res.plan})` });
      } else {
        toast({ title: "Connection Failed", description: res.error, variant: "destructive" });
      }
      queryClient.invalidateQueries({ queryKey: getListStoresQueryKey() });
    } catch (err: any) {
      toast({ title: "Test Failed", description: err.message, variant: "destructive" });
    }
  };

  return (
    <Layout>
      <div className="flex justify-between items-end mb-8">
        <div>
          <h1 className="text-3xl font-display text-gradient mb-2">Connected Stores</h1>
          <p className="text-muted-foreground">Connect Shopify stores via OAuth to start building automation flows.</p>
        </div>
        <Button onClick={() => setIsDrawerOpen(true)} className="gap-2">
          <Plus className="w-4 h-4" /> Connect Store
        </Button>
      </div>

      {isLoading ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 animate-pulse">
          {[1,2,3].map(i => <div key={i} className="h-64 bg-muted/50 rounded-2xl"></div>)}
        </div>
      ) : stores?.length === 0 ? (
        <div className="glass-panel rounded-3xl p-16 text-center border-dashed border-border">
          <StoreIcon className="w-16 h-16 mx-auto mb-6 text-slate-600" />
          <h3 className="text-xl font-display mb-2">No stores connected</h3>
          <p className="text-muted-foreground mb-8 max-w-md mx-auto">
            Connect your first Shopify store by authorizing SoraFlo via the Shopify OAuth flow.
          </p>
          <Button onClick={() => setIsDrawerOpen(true)}>Connect a Store</Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {stores?.map((store) => (
            <div key={store.id} className="glass-panel rounded-2xl p-6 relative group border border-border/50 hover:border-primary/30 transition-colors">
              <div className="flex justify-between items-start mb-6">
                <div>
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-xl font-display font-bold text-foreground">{store.nickname}</h3>
                    <span className={`px-2.5 py-1 text-[10px] uppercase tracking-wider font-bold rounded-full ${store.storeType === 'own' ? 'bg-primary/20 text-primary border border-primary/20' : 'bg-purple-500/20 text-purple-400 border border-purple-500/20'}`}>
                      {store.storeType}
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground font-mono">{store.storeUrl}</p>
                </div>
                
                <div className="flex items-center gap-2 bg-muted px-3 py-1.5 rounded-lg border border-border/50">
                  <div className={`w-2 h-2 rounded-full ${
                    store.status === 'active' ? 'bg-emerald-400 shadow-[0_0_8px_#34d399]' : 
                    store.status === 'error' ? 'bg-rose-500 shadow-[0_0_8px_#f43f5e]' : 
                    'bg-slate-500'
                  }`} />
                  <span className="text-xs font-medium capitalize">{store.status}</span>
                </div>
              </div>

              <div className="space-y-3 mb-8 bg-muted/50 p-4 rounded-xl border border-border/50">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Client ID</span>
                  <span className="font-mono text-foreground">{store.apiKeyMasked ?? "••••••••"}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Access Token</span>
                  <span className="font-mono text-foreground">{store.accessTokenMasked ?? "••••••••"}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">API Version</span>
                  <span className="font-mono text-foreground">{store.apiVersion}</span>
                </div>
                {store.lastTestedAt && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Last Tested</span>
                    <span className="text-foreground">{fmtDateTime(store.lastTestedAt)}</span>
                  </div>
                )}
              </div>

              <div className="flex gap-3">
                <Button 
                  variant="secondary" 
                  className="flex-1 gap-2" 
                  onClick={() => handleTest(store.id)}
                  isLoading={testMutation.isPending && testMutation.variables?.id === store.id}
                >
                  <Zap className="w-4 h-4" /> Test Connection
                </Button>
                <Button 
                  variant="destructive" 
                  size="icon"
                  onClick={() => handleDelete(store.id)}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <OAuthDrawer 
        isOpen={isDrawerOpen} 
        onClose={() => setIsDrawerOpen(false)} 
      />
    </Layout>
  );
}

function OAuthDrawer({ isOpen, onClose }: { isOpen: boolean, onClose: () => void }) {
  const { toast } = useToast();
  const token = useAuthStore(s => s.token);
  const [isLoading, setIsLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    nickname: '',
    storeUrl: '',
    storeType: 'own' as 'own' | 'partner',
    clientId: '',
    clientSecret: '',
    apiVersion: '2025-01',
  });

  const handleConnect = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const res = await fetch(`${BASE_URL}/api/stores/oauth/start`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
        },
        body: JSON.stringify(formData),
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.message || "Failed to start OAuth");
      }

      // Redirect user to Shopify to authorize the app
      window.location.href = data.authUrl;
    } catch (err: any) {
      toast({ title: "Failed to initiate OAuth", description: err.message, variant: "destructive" });
      setIsLoading(false);
    }
  };

  return (
    <Drawer isOpen={isOpen} onClose={onClose} title="Connect Shopify Store">
      <form onSubmit={handleConnect} className="space-y-5">
        <div className="bg-primary/10 border border-primary/20 p-4 rounded-xl space-y-3">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-primary shrink-0 mt-0.5" />
            <div className="text-xs text-primary/80 leading-relaxed space-y-2">
              <p className="font-semibold text-primary">How to get your credentials:</p>
              <ol className="list-decimal list-inside space-y-1">
                <li>Go to the <strong>Shopify Partner Dashboard</strong> → Apps → Create app</li>
                <li>Under <strong>Configuration</strong>, add the required API scopes, including <strong>read_files</strong> and <strong>write_files</strong>. PDF-to-draft-order imports also require <strong>read_products</strong>, <strong>read_customers</strong>, <strong>read_draft_orders</strong> and <strong>write_draft_orders</strong>, with access to customer data.</li>
                <li>Copy your <strong>Client ID</strong> and <strong>Client Secret</strong></li>
                <li>Add this callback URL to your app's <strong>Allowed redirect URLs</strong>:</li>
              </ol>
              <code className="block bg-muted/50 px-2 py-1 rounded text-[10px] break-all text-primary mt-1">
                {`${window.location.protocol}//${window.location.host}/api/stores/oauth/callback`}
              </code>
            </div>
          </div>
        </div>

        <div className="space-y-2">
          <Label>Nickname</Label>
          <Input 
            placeholder="e.g. Main US Store" 
            value={formData.nickname} 
            onChange={e => setFormData({ ...formData, nickname: e.target.value })} 
            required 
          />
        </div>

        <div className="space-y-2">
          <Label>Store URL</Label>
          <Input 
            placeholder="your-store.myshopify.com" 
            value={formData.storeUrl} 
            onChange={e => setFormData({ ...formData, storeUrl: e.target.value })} 
            required 
          />
        </div>

        <div className="space-y-2">
          <Label>Store Type</Label>
          <Select 
            value={formData.storeType} 
            onChange={e => setFormData({ ...formData, storeType: e.target.value as any })}
          >
            <option value="own">Own Store</option>
            <option value="partner">Partner / Supplier</option>
          </Select>
        </div>

        <div className="pt-4 border-t border-border/50 space-y-5">
          <div className="space-y-2">
            <Label>Client ID</Label>
            <Input 
              placeholder="From the Partner Dashboard" 
              value={formData.clientId} 
              onChange={e => setFormData({ ...formData, clientId: e.target.value })} 
              required 
            />
          </div>

          <div className="space-y-2">
            <Label>Client Secret</Label>
            <Input 
              type="password"
              placeholder="From the Partner Dashboard" 
              value={formData.clientSecret} 
              onChange={e => setFormData({ ...formData, clientSecret: e.target.value })} 
              required 
            />
          </div>

          <div className="space-y-2">
            <Label>API Version</Label>
            <Input 
              placeholder="2025-01" 
              value={formData.apiVersion} 
              onChange={e => setFormData({ ...formData, apiVersion: e.target.value })} 
              required 
            />
          </div>
        </div>

        <div className="pt-6">
          <Button type="submit" className="w-full gap-2" isLoading={isLoading}>
            <ExternalLink className="w-4 h-4" />
            Authorize with Shopify
          </Button>
          <p className="text-center text-xs text-muted-foreground mt-3">
            You'll be redirected to Shopify to approve the connection.
          </p>
        </div>
      </form>
    </Drawer>
  );
}
